import React, { useState, useCallback, useMemo } from 'react';
import { jsPDF } from 'jspdf';

// Assessment data
const ASSESSMENT_DATA = {
  phases: [
    {
      id: 'navigate',
      name: 'Navigate Phase',
      maxPoints: 25,
      questions: [
        {
          id: 'q1',
          number: 1,
          title: 'Role Selection Approach',
          maxPoints: 5,
          options: [
            { id: 'q1_opt1', text: 'We try to plan succession for all management positions simultaneously', points: 1 },
            { id: 'q1_opt2', text: 'We focus on positions where current holders are near retirement', points: 2 },
            { id: 'q1_opt3', text: 'We identify critical roles based on operational impact analysis', points: 3 },
            { id: 'q1_opt4', text: 'We systematically select target roles using specific criteria including immediate need, impact, feasibility, and resources', points: 5 }
          ]
        },
        {
          id: 'q2',
          number: 2,
          title: 'Process Setup',
          maxPoints: 5,
          options: [
            { id: 'q2_opt1', text: 'We discuss succession planning only when someone announces departure', points: 1 },
            { id: 'q2_opt2', text: 'We have informal discussions about succession occasionally', points: 2 },
            { id: 'q2_opt3', text: 'We review succession annually but without systematic process', points: 3 },
            { id: 'q2_opt4', text: 'We have systematic process setup with timeline, success criteria, stakeholders, resources, and monitoring for specific target roles', points: 5 }
          ]
        },
        {
          id: 'q3',
          number: 3,
          title: 'Timeline Management',
          maxPoints: 5,
          options: [
            { id: 'q3_opt1', text: 'We hope current leaders will give adequate notice before leaving', points: 1 },
            { id: 'q3_opt2', text: 'We track retirement eligibility dates', points: 2 },
            { id: 'q3_opt3', text: 'We assess flight risk occasionally for key positions', points: 3 },
            { id: 'q3_opt4', text: 'We systematically assess flight risk using 12-indicator framework and adjust succession timeline accordingly', points: 5 }
          ]
        },
        {
          id: 'q4',
          number: 4,
          title: 'Resource Allocation',
          maxPoints: 5,
          options: [
            { id: 'q4_opt1', text: "We don't allocate specific resources to succession planning", points: 1 },
            { id: 'q4_opt2', text: 'We budget for external search when succession occurs', points: 2 },
            { id: 'q4_opt3', text: 'We provide some development opportunities for potential successors', points: 3 },
            { id: 'q4_opt4', text: 'We commit specific management time, budget, and resources to systematic succession planning for target roles', points: 5 }
          ]
        },
        {
          id: 'q5',
          number: 5,
          title: 'Focus Clarity',
          maxPoints: 5,
          options: [
            { id: 'q5_opt1', text: "We're not sure which positions need succession planning most", points: 1 },
            { id: 'q5_opt2', text: 'We plan succession for positions where leaders might retire soon', points: 2 },
            { id: 'q5_opt3', text: 'We focus on the most senior positions regardless of succession readiness', points: 3 },
            { id: 'q5_opt4', text: 'We systematically identify and focus on specific roles that combine immediate need with development feasibility', points: 5 }
          ]
        }
      ]
    },
    {
      id: 'evaluate',
      name: 'Evaluate Phase',
      maxPoints: 30,
      questions: [
        {
          id: 'q6',
          number: 6,
          title: 'Candidate Identification',
          maxPoints: 5,
          options: [
            { id: 'q6_opt1', text: 'We promote based on tenure or availability', points: 1 },
            { id: 'q6_opt2', text: 'We consider several internal candidates when succession occurs', points: 2 },
            { id: 'q6_opt3', text: 'We maintain informal lists of potential successors', points: 3 },
            { id: 'q6_opt4', text: 'We systematically identify candidates using cultural fit assessment and succession candidate criteria', points: 5 }
          ]
        },
        {
          id: 'q7',
          number: 7,
          title: 'Competency Assessment',
          maxPoints: 10,
          options: [
            { id: 'q7_opt1', text: 'We assume good performers will be good leaders', points: 2 },
            { id: 'q7_opt2', text: 'We use annual performance reviews to assess leadership potential', points: 4 },
            { id: 'q7_opt3', text: 'We assess candidates using general leadership competencies', points: 6 },
            { id: 'q7_opt4', text: 'We systematically evaluate candidates using 8 credit union-specific competencies with behavioral indicators', points: 10 }
          ]
        },
        {
          id: 'q8',
          number: 8,
          title: 'Gap Analysis',
          maxPoints: 5,
          options: [
            { id: 'q8_opt1', text: "We don't systematically identify development needs", points: 1 },
            { id: 'q8_opt2', text: 'We identify obvious skill gaps informally', points: 2 },
            { id: 'q8_opt3', text: 'We conduct general leadership assessments', points: 3 },
            { id: 'q8_opt4', text: 'We systematically identify specific competency gaps using target role requirements', points: 5 }
          ]
        },
        {
          id: 'q9',
          number: 9,
          title: 'Readiness Classification',
          maxPoints: 5,
          options: [
            { id: 'q9_opt1', text: 'We use subjective judgment about candidate readiness', points: 1 },
            { id: 'q9_opt2', text: "We classify candidates as 'ready' or 'not ready'", points: 2 },
            { id: 'q9_opt3', text: 'We use general readiness categories', points: 3 },
            { id: 'q9_opt4', text: 'We systematically classify candidates using 4-tier readiness model with specific criteria', points: 5 }
          ]
        },
        {
          id: 'q10',
          number: 10,
          title: 'Selection Process',
          maxPoints: 5,
          options: [
            { id: 'q10_opt1', text: 'We choose candidates based on subjective preference', points: 1 },
            { id: 'q10_opt2', text: 'We consider multiple factors informally', points: 2 },
            { id: 'q10_opt3', text: 'We use structured interview process', points: 3 },
            { id: 'q10_opt4', text: 'We use systematic succession scorecard with weighted criteria', points: 5 }
          ]
        }
      ]
    },
    {
      id: 'xccelerate',
      name: 'Xccelerate Phase',
      maxPoints: 30,
      questions: [
        {
          id: 'q11',
          number: 11,
          title: 'Development Planning',
          maxPoints: 10,
          options: [
            { id: 'q11_opt1', text: 'We send people to generic leadership programs', points: 2 },
            { id: 'q11_opt2', text: 'We provide various development opportunities', points: 4 },
            { id: 'q11_opt3', text: 'We create development plans based on performance review discussions', points: 6 },
            { id: 'q11_opt4', text: 'We create systematic preparation plans targeting specific competency gaps using 70-20-10 model', points: 10 }
          ]
        },
        {
          id: 'q12',
          number: 12,
          title: 'Development Implementation',
          maxPoints: 10,
          options: [
            { id: 'q12_opt1', text: 'Development happens when convenient', points: 2 },
            { id: 'q12_opt2', text: 'We provide training and hope it helps', points: 4 },
            { id: 'q12_opt3', text: 'We provide structured development programs', points: 6 },
            { id: 'q12_opt4', text: 'We implement gap-based development with challenging assignments, coaching, and targeted training', points: 10 }
          ]
        },
        {
          id: 'q13',
          number: 13,
          title: 'Progress Monitoring',
          maxPoints: 10,
          options: [
            { id: 'q13_opt1', text: 'We assume development activities produce results', points: 2 },
            { id: 'q13_opt2', text: 'We ask candidates how development is going', points: 4 },
            { id: 'q13_opt3', text: 'We track development activity completion', points: 6 },
            { id: 'q13_opt4', text: 'We systematically assess readiness progress using 4-dimensional framework with monthly reviews', points: 10 }
          ]
        }
      ]
    },
    {
      id: 'transition',
      name: 'Transition Phase',
      maxPoints: 15,
      questions: [
        {
          id: 'q14',
          number: 14,
          title: 'Succession Decision-Making',
          maxPoints: 5,
          options: [
            { id: 'q14_opt1', text: 'We make succession decisions based on gut feelings', points: 1 },
            { id: 'q14_opt2', text: 'We choose the most obvious candidate', points: 2 },
            { id: 'q14_opt3', text: 'We consider multiple factors when making succession decisions', points: 3 },
            { id: 'q14_opt4', text: 'We make systematic succession conclusions across 4 time horizons using assessment data', points: 5 }
          ]
        },
        {
          id: 'q15',
          number: 15,
          title: 'Scenario Planning',
          maxPoints: 5,
          options: [
            { id: 'q15_opt1', text: 'We plan for one succession scenario', points: 1 },
            { id: 'q15_opt2', text: 'We have basic backup plans', points: 2 },
            { id: 'q15_opt3', text: 'We consider emergency succession possibilities', points: 3 },
            { id: 'q15_opt4', text: 'We systematically plan across emergency, short-term, medium-term, and long-term horizons', points: 5 }
          ]
        },
        {
          id: 'q16',
          number: 16,
          title: 'Implementation Planning',
          maxPoints: 5,
          options: [
            { id: 'q16_opt1', text: 'We figure out implementation when succession happens', points: 1 },
            { id: 'q16_opt2', text: 'We have informal ideas about succession timing', points: 2 },
            { id: 'q16_opt3', text: 'We discuss implementation requirements', points: 3 },
            { id: 'q16_opt4', text: 'We create detailed implementation plans with resources, timelines, and success factors for different scenarios', points: 5 }
          ]
        }
      ]
    }
  ],
  scoringLevels: [
    { min: 80, max: 100, level: 'Systematic succession planning', description: "You're implementing many NEXT Method principles. Your organization has a mature, systematic approach to succession planning with clear processes, regular assessments, and structured development programs." },
    { min: 60, max: 79, level: 'Structured approach', description: 'Good foundation with opportunities for systematic improvement. You have established processes but could benefit from more systematic implementation and regular monitoring.' },
    { min: 40, max: 59, level: 'Basic succession planning', description: 'Significant opportunities to implement systematic processes. Your organization recognizes the importance of succession planning but lacks comprehensive systems and regular execution.' },
    { min: 20, max: 39, level: 'Informal approach', description: 'Substantial benefits available from systematic implementation. Succession planning happens reactively with minimal structure or planning.' },
    { min: 0, max: 19, level: 'Crisis-driven succession', description: 'Urgent need for systematic succession planning. Your organization currently manages succession only when forced by circumstances, creating significant risk.' }
  ]
};

// Get total questions count
const TOTAL_QUESTIONS = ASSESSMENT_DATA.phases.reduce((sum, phase) => sum + phase.questions.length, 0);

const PlanningAssessment = () => {
  const [currentView, setCurrentView] = useState('start'); // 'start', 'assessment', 'results'
  const [responses, setResponses] = useState({});

  // Calculate progress
  const progress = useMemo(() => {
    const answered = Object.keys(responses).length;
    return {
      answered,
      total: TOTAL_QUESTIONS,
      percentage: (answered / TOTAL_QUESTIONS) * 100
    };
  }, [responses]);

  // Calculate phase scores
  const phaseScores = useMemo(() => {
    const scores = {};
    ASSESSMENT_DATA.phases.forEach(phase => {
      let phaseScore = 0;
      phase.questions.forEach(question => {
        if (responses[question.id]) {
          phaseScore += responses[question.id].points;
        }
      });
      scores[phase.id] = {
        score: phaseScore,
        maxScore: phase.maxPoints,
        percentage: (phaseScore / phase.maxPoints) * 100
      };
    });
    return scores;
  }, [responses]);

  // Calculate total score
  const totalScore = useMemo(() => {
    return Object.values(responses).reduce((sum, response) => sum + response.points, 0);
  }, [responses]);

  // Get assessment level
  const assessmentLevel = useMemo(() => {
    return ASSESSMENT_DATA.scoringLevels.find(level => totalScore >= level.min && totalScore <= level.max);
  }, [totalScore]);

  // Handle option selection
  const handleOptionSelect = useCallback((questionId, optionId, points) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: { optionId, points }
    }));
  }, []);

  // Start assessment
  const startAssessment = useCallback(() => {
    setCurrentView('assessment');
  }, []);

  // Show results
  const showResults = useCallback(() => {
    setCurrentView('results');
  }, []);

  // Restart assessment
  const restartAssessment = useCallback(() => {
    setResponses({});
    setCurrentView('start');
  }, []);

  // Export JSON
  const exportJSON = useCallback(() => {
    const exportData = {
      timestamp: new Date().toISOString(),
      totalScore,
      maxScore: 100,
      percentage: totalScore,
      assessmentLevel: assessmentLevel,
      phaseScores,
      responses
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const fileName = `succession-assessment-${new Date().toISOString().split('T')[0]}.json`;

    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', fileName);
    link.click();
  }, [totalScore, assessmentLevel, phaseScores, responses]);

  // Generate PDF
  const generatePDF = useCallback(() => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 20;

    const checkPageBreak = (requiredSpace) => {
      if (yPos + requiredSpace > 270) {
        doc.addPage();
        yPos = 20;
      }
    };

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 60, 114);
    doc.text('Succession Planning Assessment Results', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Subtitle
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('NEXT Method Framework Evaluation', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    // Date
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Total Score Box
    doc.setFillColor(76, 175, 80);
    doc.roundedRect(margin, yPos, contentWidth, 30, 3, 3, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Your Total Score', pageWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(24);
    doc.text(`${totalScore} / 100`, pageWidth / 2, yPos + 23, { align: 'center' });
    yPos += 40;

    // Assessment Level
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 60, 114);
    doc.text(`Assessment Level: ${assessmentLevel?.level || 'N/A'}`, margin, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    const descLines = doc.splitTextToSize(assessmentLevel?.description || '', contentWidth);
    doc.text(descLines, margin, yPos);
    yPos += descLines.length * 5 + 10;

    // Phase Scores
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 60, 114);
    doc.text('Phase-by-Phase Results', margin, yPos);
    yPos += 10;

    ASSESSMENT_DATA.phases.forEach(phase => {
      checkPageBreak(15);
      const score = phaseScores[phase.id];
      const percentage = score.percentage.toFixed(0);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(phase.name, margin, yPos);

      doc.setFont('helvetica', 'normal');
      doc.text(`${score.score} / ${score.maxScore} points (${percentage}%)`, pageWidth - margin, yPos, { align: 'right' });

      // Progress bar
      yPos += 4;
      doc.setFillColor(230, 230, 230);
      doc.rect(margin, yPos, contentWidth, 4, 'F');

      const barColor = percentage >= 80 ? [76, 175, 80] : percentage >= 60 ? [255, 193, 7] : [244, 67, 54];
      doc.setFillColor(...barColor);
      doc.rect(margin, yPos, contentWidth * (percentage / 100), 4, 'F');
      yPos += 12;
    });

    yPos += 5;

    // Recommendations based on score
    checkPageBreak(40);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 60, 114);
    doc.text('Recommended Next Steps', margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);

    const recommendations = totalScore >= 80
      ? [
          'Continue refining your systematic processes',
          'Share best practices across the organization',
          'Consider mentoring other credit unions',
          'Focus on continuous improvement and innovation'
        ]
      : totalScore >= 60
      ? [
          'Strengthen documentation of succession processes',
          'Implement more regular progress monitoring',
          'Develop standardized assessment criteria',
          'Build out your development program structure'
        ]
      : totalScore >= 40
      ? [
          'Establish formal succession planning governance',
          'Create systematic candidate identification process',
          'Implement competency-based assessments',
          'Develop structured development planning'
        ]
      : [
          'Prioritize implementing basic succession planning',
          'Identify critical roles requiring immediate attention',
          'Establish emergency succession protocols',
          'Consider external expertise to accelerate progress'
        ];

    recommendations.forEach(rec => {
      checkPageBreak(8);
      doc.text(`• ${rec}`, margin, yPos);
      yPos += 6;
    });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Generated by SuccessionNow - The NEXT Method', pageWidth / 2, 285, { align: 'center' });

    // Save
    const fileName = `succession-assessment-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }, [totalScore, assessmentLevel, phaseScores]);

  // Render Start Screen
  if (currentView === 'start') {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white px-8 py-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Role-Specific Succession Planning Assessment</h2>
          <p className="text-blue-100">Evaluate your succession planning approach with the NEXT Method framework</p>
        </div>

        <div className="p-8 md:p-12 text-center">
          <h3 className="text-2xl font-bold text-blue-800 mb-4">Welcome to Your Assessment</h3>
          <p className="text-gray-600 mb-8 text-lg">
            This 15-minute assessment will help you evaluate your current succession planning practices
            and identify opportunities for improvement.
          </p>

          <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left max-w-xl mx-auto">
            <h4 className="font-semibold text-gray-800 mb-4">What You'll Discover:</h4>
            <ul className="space-y-3">
              {[
                'Your current succession planning maturity level',
                'Strengths and gaps across 4 key phases',
                'Specific areas for improvement',
                'Actionable recommendations based on your score'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-green-500 font-bold text-lg">✓</span>
                  <span className="text-gray-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            onClick={startAssessment}
            className="bg-gradient-to-r from-blue-800 to-blue-900 text-white px-10 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
          >
            Start Assessment
          </button>
        </div>
      </div>
    );
  }

  // Render Results Screen
  if (currentView === 'results') {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white px-8 py-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Your Assessment Results</h2>
          <p className="text-blue-100">NEXT Method Succession Planning Evaluation</p>
        </div>

        <div className="p-6 md:p-10">
          {/* Total Score */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-8 text-center text-white mb-8 shadow-lg">
            <p className="text-lg opacity-90 mb-2">Your Total Score</p>
            <p className="text-6xl font-bold mb-2">{totalScore}</p>
            <p className="text-lg opacity-90">out of 100 points</p>
          </div>

          {/* Phase Results */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {ASSESSMENT_DATA.phases.map(phase => {
              const score = phaseScores[phase.id];
              const percentage = score.percentage;
              const colorClass = percentage >= 80 ? 'text-green-500' : percentage >= 60 ? 'text-yellow-500' : 'text-red-500';

              return (
                <div key={phase.id} className="bg-white rounded-xl p-5 border border-gray-200 text-center">
                  <h4 className="text-blue-800 font-semibold mb-2">{phase.name}</h4>
                  <p className="text-3xl font-bold text-gray-800">{score.score}</p>
                  <p className="text-gray-500 text-sm mb-2">out of {score.maxScore} points</p>
                  <p className={`font-semibold ${colorClass}`}>{percentage.toFixed(0)}%</p>
                </div>
              );
            })}
          </div>

          {/* Assessment Level */}
          <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-blue-700 mb-8">
            <h4 className="text-blue-800 font-semibold text-lg mb-2">
              Your Assessment Level: {assessmentLevel?.level}
            </h4>
            <p className="text-gray-600">{assessmentLevel?.description}</p>
          </div>

          {/* Export Options */}
          <div className="text-center pt-6 border-t-2 border-gray-200">
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={generatePDF}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
              >
                Download PDF Report
              </button>
              <button
                onClick={exportJSON}
                className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
              >
                Export Data (JSON)
              </button>
              <button
                onClick={restartAssessment}
                className="bg-gradient-to-r from-gray-400 to-gray-500 text-white px-6 py-3 rounded-full font-semibold shadow hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
              >
                Retake Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Assessment Questions
  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header with Progress */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white px-8 py-6">
        <h2 className="text-xl md:text-2xl font-bold mb-2 text-center">Succession Planning Assessment</h2>
        <p className="text-blue-100 text-center text-sm mb-4">NEXT Method Framework Evaluation</p>

        {/* Progress Bar */}
        <div className="bg-white/20 h-2 rounded-full overflow-hidden">
          <div
            className="bg-green-400 h-full rounded-full transition-all duration-300"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
        <p className="text-center text-blue-100 text-sm mt-2">
          {progress.answered === 0 ? 'Ready to begin' :
           progress.answered === progress.total ? 'Assessment complete!' :
           `${progress.answered} of ${progress.total} questions answered`}
        </p>
      </div>

      <div className="p-6 md:p-8">
        {/* Questions by Phase */}
        {ASSESSMENT_DATA.phases.map((phase, phaseIndex) => (
          <div key={phase.id} className="mb-8" style={{ animationDelay: `${phaseIndex * 0.1}s` }}>
            {/* Phase Header */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg mb-4 border-l-4 border-blue-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-blue-800">{phase.name}</h3>
                <span className="text-sm bg-white px-3 py-1 rounded-full text-gray-600">
                  {phaseScores[phase.id]?.score || 0} / {phase.maxPoints} points
                </span>
              </div>
            </div>

            {/* Questions */}
            {phase.questions.map(question => (
              <div key={question.id} className="bg-gray-50 rounded-xl p-5 mb-4 border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <span className="font-semibold text-gray-800">
                    {question.number}. {question.title}
                  </span>
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded-full">
                    Max: {question.maxPoints} pts
                  </span>
                </div>

                <div className="space-y-2">
                  {question.options.map(option => {
                    const isSelected = responses[question.id]?.optionId === option.id;
                    return (
                      <label
                        key={option.id}
                        className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-700 shadow-md'
                            : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option.id}
                          checked={isSelected}
                          onChange={() => handleOptionSelect(question.id, option.id, option.points)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                          isSelected ? 'border-blue-700 bg-blue-700' : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-700">{option.text}</p>
                          <p className="text-sm text-gray-500 font-semibold mt-1">
                            {option.points} point{option.points > 1 ? 's' : ''}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Submit Button */}
        {progress.answered === progress.total && (
          <div className="text-center pt-4">
            <button
              onClick={showResults}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-10 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
            >
              Calculate Results
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanningAssessment;
